import { React, useState, useEffect } from 'react';
import { Grid, Box, Button, Paper, Stack, FormControl, InputLabel, Select, MenuItem, FormHelperText, Typography, Checkbox, FormControlLabel } from '@mui/material';
import { LoadingOutlined } from '@ant-design/icons';
import axios from 'axios';
import { HiOutlineCurrencyRupee } from "react-icons/hi2";
import { getYearList } from '../../services/yearList';
import { getMonthList } from '../../services/monthList';
import Popup from '../Popup';
import TaxSummaryTable from '../table-data/TaxSummaryTable';
import LoaderOverlay from '../LoaderOverlay';
import SelectEmployeeTable from '../table-data/SelectEmployeeTable';
import CreatedPayrollTable from '../table-data/CreatedPayrollTable';
import '../../App.css';
import { Link, useNavigate } from 'react-router-dom';
import Notification from '../Notification';
import { setLoaderMessage } from '../../utils/loaderManager';
import { useSelector } from 'react-redux';
import { getStoredCompanyDetails, getCompanyDisplayFromDetails, getShortCompanyName } from '../../services/companyService';

// Custom component for required field label
const RequiredLabel = ({ label }) => (
  <span>
    {label}
    <span style={{ color: 'red', marginLeft: '3px' }}>*</span>
  </span>
);

const yearOptions = getYearList();
const monthOptions = getMonthList();

// Set initial values to current month and year
const today = new Date();
const currentYear = today.getFullYear().toString();
const currentMonthIndex = today.getMonth();
const currentMonthName = monthOptions[currentMonthIndex].title;

const initialFValues = {
  id: 0,
  month: currentMonthName,
  year: currentYear,
  employeesSelected: []  // Format: [{ employee_id: 301, isTds: true }, ...]
}

function CreatePayroll() {

  const [values, setValues] = useState(initialFValues);
  const [isSending, setIsSending] = useState(false);
  const [errors, setErrors] = useState({});
  const [payrolls, setPayrolls] = useState([]);
  const [selectedEmpIDs, setSelectedEmpIDs] = useState([]);
  const [openPopup, setOpenPopup] = useState(false);
  const [openTaxPopup, setOpenTaxPopup] = useState(false);
  const [taxSummaryData, setTaxSummaryData] = useState([]);
  const [isTaxLoading, setIsTaxLoading] = useState(false);
  const [open, setOpen] = useState(true);
  const [showPDF, setShowPDF] = useState(false);
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' });
  const navigate = useNavigate();
  const companyDetails = useSelector((state) => state.auth.activeCompanyDetails) || getStoredCompanyDetails();
  const { companyName: globalCompanyName } = getCompanyDisplayFromDetails(companyDetails || {});

  // Reset employee data when month or year changes
  useEffect(() => {
    resetEmployeeData();
  }, [values.month, values.year]);

  // Compute financial year string (e.g., "2024-2025") from selected month and year.
  const getFinancialYearFromMonthYear = (monthName, yearStr) => {
    const monthIndex = monthOptions.findIndex(m => m.title === monthName);
    const yearNum = parseInt(yearStr, 10) || new Date().getFullYear();
    // Assuming months are Jan (0) ... Dec (11). Financial year Apr-Mar.
    if (monthIndex >= 3) {
      return `${yearNum}-${yearNum + 1}`;
    } else {
      return `${yearNum - 1}-${yearNum}`;
    }
  };

  const fetchTaxSummaryForCreatePayroll = async () => {
    const financialYear = getFinancialYearFromMonthYear(values.month, values.year);
    setIsTaxLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/create-payroll/get-emp-tax-summary/${financialYear}`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });

      let responseData = null;
      if (Array.isArray(response.data)) {
        responseData = response.data;
      } else if (response.data && response.data.success && response.data.data) {
        responseData = response.data.data;
      } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
        responseData = response.data.data;
      }

      if (responseData && Array.isArray(responseData)) {
        setTaxSummaryData(responseData);
      } else {
        setTaxSummaryData([]);
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setNotify({ isOpen: true, message: 'Session Expired!', type: 'error' });
        setTimeout(() => { navigate('/'); }, 1000);
      } else {
        setTaxSummaryData([]);
        setNotify({ isOpen: true, message: err.response?.data?.message || 'Failed to fetch tax summary', type: 'error' });
      }
    } finally {
      setIsTaxLoading(false);
    }
  };

  const resetEmployeeData = () => {
    setValues({
      ...values,
      employeesSelected: []
    });
    setSelectedEmpIDs([]);
    setPayrolls([]);
  }

  const validate = () => {
    let temp = {}
    temp.year = values.year.length != 0 ? "" : "This field is required."
    temp.month = values.month.length != 0 ? "" : "This field is required."

    setErrors({
      ...temp
    })
    return Object.values(temp).every(x => x == "")
  }

  // Receives payload in format: [{ employee_id: 301, isTds: true }, ...]
  let handleSelectedEmployees = (selectedEmployees) => {
    setValues({
      ...values,
      employeesSelected: selectedEmployees
    });
  }

  const resetForm = () => {
    setValues(initialFValues);
    setErrors({});
    setSelectedEmpIDs([]);
    setPayrolls([]);
  }

  const convertToDefEventPara = (name, value) => ({
    target: {
      name: name,
      value: value
    }
  })

  function handleInputChange(e) {
    const { name, value } = e.target;
    setValues({
      ...values,
      [name]: value
    });
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validate()) return; // Also stop if validation fails

    if (values?.employeesSelected?.length > 0) {
      setIsSending(true); // Start loading state
      setLoaderMessage('Processing payroll...');

      // Check if any employee has TDS checkbox ticked
      const hasTdsSelected = values.employeesSelected.some(emp => emp.isTds === true);

      // If TDS is selected, show "Computing TDS..." message after 3 seconds
      let tdsMessageTimeout = null;
      if (hasTdsSelected) {
        tdsMessageTimeout = setTimeout(() => {
          setLoaderMessage('Computing TDS...');
        }, 3000);
      }

      axios.post(`${process.env.REACT_APP_API_BASE_URL}/create-payroll/process`, values)
        .then(function (response) {
          setOpenPopup(true);
          setPayrolls(response.data);
        })
        .catch(function (err) {
          if (err.response) {
            if (err.response.status === 401) {
              setNotify({
                isOpen: true,
                message: 'Session Expired!',
                type: 'error'
              });
              setTimeout(() => {
                navigate('/');
              }, 1000);
            } else if (err.response.status === 400) {
              // Show backend validation/business error (e.g., tax slab not configured)
              const backendMessage =
                err.response.data?.error ||
                err.response.data?.message ||
                'Unable to process payroll. Please check configuration.';
              setNotify({
                isOpen: true,
                message: backendMessage,
                type: 'error'
              });
            } else {
              setNotify({
                isOpen: true,
                message: err.response.data?.message || 'Failed to process payroll',
                type: 'error'
              });
            }
          } else {
            setNotify({
              isOpen: true,
              message: 'Network error. Please try again.',
              type: 'error'
            });
          }
        })
        .finally(() => {
          // Clear the timeout if the request completes before the message change
          if (tdsMessageTimeout) {
            clearTimeout(tdsMessageTimeout);
          }
          setIsSending(false); // End loading state
        });
    } else {
      setNotify({
        isOpen: true,
        message: 'Please Select Employees First!',
        type: 'error'
      });
    }
  }

  const generatePayslipsHandler = async (payrolls) => {
    if (payrolls?.length > 0) {
      const data = {
        id: 1,
        month: values.month,
        year: values.year,
        payrolls: payrolls,
      }

      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/salary-slip/generate-salary-slips`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(data),
        });

        response.blob().then(blob => {
          let url = window.URL.createObjectURL(blob);
          let a = document.createElement('a');
          a.href = url;
          a.download = `${getShortCompanyName(globalCompanyName) || 'Report'} Report for Payslips ${values.month.substring(0, 3)} ${values.year}.zip`;
          a.click();
        });

        if (response.status === 422) {
          setNotify({
            isOpen: true,
            message: 'Please set Company Logo First!',
            type: 'error'
          });
        }
        else if (response.status === 401) {
          setNotify({
            isOpen: true,
            message: 'Session Expired!',
            type: 'error'
          });
          setTimeout(() => {
            navigate('/');
          }, 1000);
        }
      } catch (e) {
      }
    } else {
      setNotify({
        isOpen: true,
        message: 'Please Create Payroll First!',
        type: 'error'
      });
    }
  }

  const sendPayslipsHandler = async (payrolls) => {
    if (payrolls?.length > 0) {
      const data = {
        id: 1,
        month: values.month,
        year: values.year,
        payrolls: payrolls,
      }
      setIsSending(true);
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/salary-slip/send-salary-slips`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(data),
        });
        const json = await response.json();
        if (response.status === 200) {
          setNotify({
            isOpen: true,
            message: json.message,
            type: 'success'
          });
        } else {
          setNotify({
            isOpen: true,
            message: json.message || 'Some error has occured at Server',
            type: 'error'
          });
        }
      } catch (e) {
        if (e.response && e.response.status === 401) {
          setNotify({
            isOpen: true,
            message: 'Session Expired!',
            type: 'error'
          });
          setTimeout(() => {
            navigate('/');
          }, 1000);
        }
      }
      setIsSending(false);
    } else {
      setNotify({
        isOpen: true,
        message: 'Please Create Payroll and Generate Payslip First!',
        type: 'error'
      });
    }
  }

  return (
    <Box sx={{ display: "flex", justifyContent: "center", margin: "4px 6px", padding: "4px 6px", fontWeight: "500px" }}>
      <Paper elevation={4} >
        <Grid className="form-title" container spacing={1} sx={{ py: 0.5, px: 1.5 }}>
          <Grid item> <Typography variant="h6" sx={{ textAlign: "center", fontWeight: 600 }}>Create Payroll</Typography> </Grid>
          <Grid item> <HiOutlineCurrencyRupee size={28} /> </Grid>
        </Grid>
        <form className="classes.root" autoComplete="off" onSubmit={handleSubmit}>

          <Stack spacing={1} sx={{ margin: "6px", marginTop: '0px' }}>

            <Stack display="flex" spacing={1.5} direction="row" sx={{ alignItems: "center", justifyContent: "space-between" }}>

              <FormControl
                variant="outlined"
                size="small"
                {...(errors.month && { error: true })}>
                <InputLabel><RequiredLabel label="Select Month" /></InputLabel>
                <Select
                  label="Select Month"
                  name="month"
                  size="small"
                  value={values.month}
                  onChange={handleInputChange}
                  style={{ width: "250px" }}
                >
                  <MenuItem value="">None</MenuItem>
                  {
                    monthOptions.map(
                      (item, index) => (
                        <MenuItem key={item.id} value={item.title} disabled={index > currentMonthIndex && values.year === currentYear}>
                          {item.title}
                        </MenuItem>
                      )
                    )
                  }
                </Select>
                {errors.month && <FormHelperText>{errors.month}</FormHelperText>}
              </FormControl>

              <FormControl
                variant="outlined"
                size="small"
                {...(errors.year && { error: true })}>
                <InputLabel><RequiredLabel label="Select Year" /></InputLabel>
                <Select
                  label="Select Year"
                  name="year"
                  size="small"
                  value={values.year}
                  onChange={handleInputChange}
                  style={{ width: "250px" }}
                >
                  <MenuItem value="">None</MenuItem>
                  {
                    yearOptions.map(
                      item => (<MenuItem key={item.id} value={item.title}>{item.title}</MenuItem>)
                    )
                  }
                </Select>
                {errors.year && <FormHelperText>{errors.year}</FormHelperText>}
              </FormControl>
            </Stack>

            <SelectEmployeeTable
              selectedMonth={values.month}
              selectedYear={values.year}
              onSelectEmployees={handleSelectedEmployees}
              key={`${values.month}-${values.year}`}
            />

            

            <Grid container className="space-around" sx={{ py: 0.5 }} spacing={1}>
              <Grid item>
                <Button size="small" variant="outlined" color="secondary" type="submit" disabled={isSending} sx={{ minHeight: 32 }}>
                  {isSending ? (
                    <>
                      Processing...
                      <LoadingOutlined style={{ marginLeft: '6px' }} />
                    </>
                  ) : (
                    'Create Payroll'
                  )}
                </Button>
              </Grid>
              <Grid item>
                <Button size="small" variant="outlined" color="primary" onClick={() => generatePayslipsHandler(payrolls)} sx={{ minHeight: 32 }}>Generate Payslip</Button>
              </Grid>
              <Grid item>
                <Button size="small" variant="outlined" color="error" onClick={() => sendPayslipsHandler(payrolls)} sx={{ minHeight: 32 }}>
                  Send Payslip {isSending && <LoadingOutlined style={{ marginLeft: '6px' }} />}
                </Button>
              </Grid>
              <Grid item>
                <Link to='email-send' class='link'>
                  <Button size="small" variant="outlined" color="success" sx={{ minHeight: 32 }}>Send Mail</Button>
                </Link>
              </Grid>
              {isSending && (
                <Grid item xs={12} sx={{ marginTop: '4px' }}>
                  <Typography variant="body2" color="textSecondary">
                    Processing Payroll..
                  </Typography>
                </Grid>
              )}
            </Grid>

          </Stack>
        </form>

        <Popup
          title={`Processed Payrolls - ${values.month} ${values.year}`}
          openPopup={openPopup}
          setOpenPopup={setOpenPopup}
          sx={{
            '& .MuiPaper-root': {
              maxHeight: '600px', // Adjust based on your design
              overflow: 'hidden',
              width: '800px', // Optional: control width
            },

          }}
        >
          <Box
            sx={{
              maxHeight: '500px', // Fixed height for table container
              overflowY: 'auto',
              overflowX: 'auto',
            }}
          >
            <CreatedPayrollTable payrolls={payrolls} />
          </Box>
        </Popup>
        <Popup
          title={`Tax Summary - ${values.month} ${values.year}`}
          openPopup={openTaxPopup}
          setOpenPopup={setOpenTaxPopup}
          sx={{
            '& .MuiPaper-root': {
              maxHeight: '600px',
              overflow: 'hidden',
              width: '700px',
            },
          }}
        >
          <Box sx={{ maxHeight: '500px', overflowY: 'auto', overflowX: 'auto', padding: 2 }}>
            <TaxSummaryTable data={taxSummaryData} isLoading={isTaxLoading} />
          </Box>
        </Popup>
      </Paper>
      <Notification
        notify={notify}
        setNotify={setNotify}
      />
      <LoaderOverlay open={isSending} message="Processing Payroll.." />
    </Box>
  )
}

export default CreatePayroll